import { FormEvent, useState } from 'react';
import { useFrontendAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getSiteSettings, submitTicket } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { ValidationError } from '../admin/components/ValidationError';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';

const DEFAULT_CONTACT_EMAIL = 'support@petito.be';

const Helpdesk = () => {
    const { user } = useFrontendAuth();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);

    const { data: settings } = useQuery({
        queryKey: ['siteSettings', 'helpdesk'],
        queryFn: getSiteSettings,
        staleTime: 10 * 60 * 1000,
    });

    const contactEmail = settings?.contactEmail || DEFAULT_CONTACT_EMAIL;

    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!subject.trim() || !message.trim()) {
            setError(tr('helpdesk.ticket.required', 'Subject and message are required.'));
            scrollToFirstErrorField();
            return;
        }
        setSubmitting(true);
        try {
            await submitTicket({
                subject: subject.trim(),
                message: message.trim(),
            });
            setSuccess(true);
            setSubject('');
            setMessage('');
        } catch (err: unknown) {
            let msg = tr('helpdesk.ticket.error', 'Could not submit. Please try again.');
            if (err && typeof err === 'object' && 'response' in err) {
                const data = (err as { response?: { data?: { message?: string } } }).response?.data;
                if (data?.message) msg = data.message;
            }
            setError(msg);
            scrollToFirstErrorField();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mx-auto max-w-2xl py-6 sm:py-12">
            <h1 className="mb-4 text-2xl font-bold sm:mb-6 sm:text-4xl">{t('page.helpdesk.title')}</h1>
            <p className="mb-6 text-base sm:mb-8 sm:text-lg">{t('page.helpdesk.subtitle')}</p>

                {/* In-app ticket \u2014 lands in Admin \u2192 Support tickets */}
            <div className="mb-8 rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_60%)] bg-white p-6 shadow sm:p-8">
                <h2 className="mb-2 text-lg font-semibold text-[var(--text-color)] sm:text-xl">
                    {tr('helpdesk.ticket.heading', 'Send a support ticket')}
                </h2>
                <p className="mb-4 text-sm text-gray-600">
                    {tr(
                        'helpdesk.ticket.blurb',
                        'Describe your issue below. Our team will see it in the admin panel and get back to you.'
                    )}
                </p>

                {success ? (
                    <div className="rounded-xl border border-[var(--accent-color)]/40 bg-[color-mix(in_srgb,var(--accent-color),transparent_90%)] p-5 text-center">
                        <p className="font-medium text-[var(--text-color)]">
                            {tr('helpdesk.ticket.success', 'Thanks \u2014 your ticket was submitted. We will get back to you soon.')}
                        </p>
                        <button
                            type="button"
                            className="mt-3 text-sm text-[var(--accent-color)] underline"
                            onClick={() => setSuccess(false)}
                        >
                            {tr('helpdesk.ticket.another', 'Send another message')}
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {user?.email && (
                            <p className="text-xs text-gray-500">
                                {tr('helpdesk.ticket.signedin', 'Signed in as {email}').replace(
                                    '{email}',
                                    user.email
                                )}
                            </p>
                        )}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-[var(--text-color)]">
                                {tr('helpdesk.ticket.subject', 'Subject')}
                            </label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                maxLength={200}
                                required
                                className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-white/80 px-3 py-2.5 text-sm text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/40"
                            />
                        </div>
                        <div>
                            <label className="mb-1 block text-sm font-medium text-[var(--text-color)]">
                                {tr('helpdesk.ticket.message', 'Message')}
                            </label>
                            <textarea
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={5}
                                maxLength={4000}
                                required
                                className="w-full rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-white/80 px-3 py-2.5 text-sm text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]/40"
                            />
                        </div>
                        <ValidationError error={error ?? undefined} />
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full rounded-xl bg-[var(--accent-color)] py-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 sm:w-auto sm:px-10"
                        >
                            {submitting
                                ? '...'
                                : tr('helpdesk.ticket.submit', 'Submit ticket')}
                        </button>
                    </form>
                )}
            </div>

            <div className="rounded-2xl bg-white p-6 text-center shadow sm:p-10">
                <p className="mb-6 text-lg sm:text-2xl">{t('page.helpdesk.sendemail')}</p>
                <a
                    href={`mailto:${contactEmail}?subject=Help Request from ${user?.userName || 'User'}&body=Hi Petito team,%0D%0A%0D%0AI have an issue with...`}
                    className="inline-flex min-h-11 w-full max-w-full items-center justify-center break-all rounded-xl bg-[var(--accent-color)] px-4 py-4 text-sm font-semibold text-white hover:opacity-90 sm:w-auto sm:px-10 sm:text-xl"
                >
                    {contactEmail}
                </a>
                <p className="mt-8 text-gray-600">{t('page.helpdesk.replytime')}</p>
            </div>

            <div className="mt-10 text-center sm:mt-12">
                <Link to="/my-account" className="inline-flex min-h-11 items-center text-[var(--accent-color)] underline">{t('common.backtoaccount')}</Link>
            </div>
        </div>
    );
};

export default Helpdesk;
